<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { defaultFunction } from '@/components/_parials/buttonCore'

export default defineComponent({
  name: 'ButtonClose',

  props: {
    color: {
      type: String as PropType<'white' | 'black' | 'gray' | 'darkGray' | 'orange'>,
      default: 'gray',
    },

    background: {
      type: String as PropType<'transparent' | 'gray' | 'orange'>,
      default: 'transparent',
    },

    iconSize: {
      type: String as PropType<'small' | 'normal' | 'large'>,
      default: 'normal',
    },

    /**
     * 幅設定
     * @description 基本的にはレイアウトラッパーの DOM を用意してレイアウト側で幅をコントロールすること
     *              やむを得ずラッパー側で幅が指定できない（auto、もしくは 100% にせざるを得ない）場合に本 props を使用し、幅の設定を要素側に持たせる
     */
    width: {
      type: Number,
      default: undefined,
    },

    shape: {
      type: String as PropType<'rect' | 'circle'>,
      default: 'circle',
    },

    emitName: {
      type: String,
      default: 'click:close',
    },

    /**
     * クリック時に prevent default させるかどうか
     */
    prevent: {
      type: Boolean,
      default: false,
    },

    /**
     * クリック時に stop propagation させるかどうか
     */
    stop: {
      type: Boolean,
      default: false,
    },

    hasTooltip: {
      type: Boolean,
      default: true,
    },
  },

  setup(props, { emit }) {
    const {
      // methods
      runEvent,
    } = defaultFunction(props, emit)

    return {
      // methods
      runEvent,
    }
  }
})
</script>

<template>
  <div
    :class="[
      'o-action__close',
      hasTooltip ? 'u-action__tooltip' : undefined,
    ]"
    :data-tooltip="hasTooltip ? '閉じる' : undefined"
    :data-color="color"
    :data-background="background"
    :data-shape="shape"
    :data-icon-size="iconSize"
    :style="{ '--max-width': width ? `${width}px` : 'none' }">
    <button
      class="o-action__close--item"
      type="button"
      @click="runEvent">
      <!--
        アクセシビリティ用ラベル要素
        詳細はスタイル側のコメント参照
      -->
      <span class="o-action__close--label">
        閉じる
      </span>
    </button>
  <!-- /.o-action__close --></div>
</template>

<style lang="sass" scoped>
.o-action__close
  $root: &
  $button-small: 0.8em
  $button-normal: 1.2em
  $button-large: 2em

  width: 100%
  height: 100%
  max-width: var(--max-width)
  display: flex
  align-items: center
  justify-content: center

  & &--item
    +iconCross($weight: 0.18em)
    width: 100%
    height: 100%
    color: inherit
    position: relative
    padding: 1em
    +transition

  // アクセシビリティ上の観点で、
  // button 要素直下に視覚的に表出しないテキストノードを配置するのは推奨されないため、
  // button 配下にアクセシビリティ用のラベル要素（span）を配置する
  & &--label
    width: 1px
    height: 1px
    position: absolute
    overflow: hidden
    clip: rect(0, 0, 0, 0)
    white-space: nowrap

  &[data-color="white"]
    #{$root}--item
      &::before,
      &::after
        background-color: rgba($bg-white, 1)

  &[data-color="black"]
    #{$root}--item
      &::before,
      &::after
        background-color: rgba($bg-black, 1)

  &[data-color="gray"]
    #{$root}--item
      &::before,
      &::after
        background-color: rgba($bg-gray, 1)

  &[data-color="darkGray"]
    #{$root}--item
      &::before,
      &::after
        background-color: rgba(#6c6c6c, 1)

  &[data-color="orange"]
    #{$root}--item
      &::before,
      &::after
        background-color: rgba($bg-orange, 1)

  &[data-background="gray"]
    #{$root}--item
      background-color: rgba($bg-gray, 1)

  &[data-background="orange"]
    #{$root}--item
      background-color: rgba($bg-orange, 1)

  &[data-icon-size="small"]
    #{$root}--item
      min-width: $button-small
      min-height: $button-small

      &::before,
      &::after
        width: $button-small

  &[data-icon-size="normal"]
    #{$root}--item
      min-width: $button-normal
      min-height: $button-normal

      &::before,
      &::after
        width: $button-normal

  &[data-icon-size="large"]
    #{$root}--item
      min-width: $button-large
      min-height: $button-large

      &::before,
      &::after
        width: $button-large

  &[data-shape="circle"]
    #{$root}--item
      border-radius: 50%

  &[data-tooltip]
    #{$root}--item
      +mouseover
        transform: scale(1.2)

  // ツールチップ非表示の場合は、マウスオーバーが分かりやすいよう回転も追加する
  &:not([data-tooltip])
    #{$root}--item
      +mouseover
        transform: scale(1.2) rotate(90deg)
</style>
